/**
 * menu-targets.ts — spis KARTOTEK SYSTEMOWYCH, które da się wstawić do menu modułu w kreatorze
 * ITE (paleta → grupa „Kartoteki"), z modułem-właścicielem decydującym o aktywności u klienta.
 *
 * Pozycja menu = `{ type: 'module', moduleCode: <slug> }` — ten sam kształt, który wstawia
 * kreator menu klienta; pasek klienta rozwiązuje slug na trasę (MODULE_LANDING_OVERRIDES).
 * Ekrany (nie kartoteki) → `tenant-screens.ts`; Ustawienia → element systemowy `settings`.
 * Kartoteki z kreatora modułów (`<szablon>_<encja>`) nie są tu wpisane — liczy je serwer z
 * opublikowanych wersji szablonów.
 *
 * Właściciel `crm_core` = pakiet bazowy (każdy klient go ma). Magazyny w menu modułu wymagają
 * modułu `warehouses` (decyzja 2026-10-02) — mimo że paleta klienta traktuje je jak pakiet bazowy.
 */

import { BASE_PACKAGE_KARTOTEKI, type MenuSourceKartoteka, SYSTEM_ITEMS } from './menu';

export interface SystemKartotekaTarget {
  /** Slug kartoteki = `moduleCode` pozycji menu. */
  slug: string;
  /** Etykieta PL (paleta + domyślna nazwa pozycji). */
  label: string;
  iconName: string;
  /** Trasa listy u klienta. */
  path: string;
  /** Kod modułu, od którego zależy kartoteka u klienta. */
  ownerModuleCode: string;
  /**
   * Czy w menu modułu można przypiąć do pozycji formularz/listę z biblioteki kartoteki.
   * `false` = kartoteka bez katalogu pól (Dokumenty KSeF) — pozycja jest samym linkiem.
   */
  hasLayouts: boolean;
}

const CRM_ENTITY_KEYS = ['users', 'employees', 'companies', 'contractors', 'contractor_contacts'];

export const SYSTEM_KARTOTEKA_TARGETS: readonly SystemKartotekaTarget[] = [
  ...SYSTEM_ITEMS.filter((s) => CRM_ENTITY_KEYS.includes(s.itemKey)).map((s) => ({
    slug: s.itemKey,
    label: s.defaultLabel,
    iconName: s.iconName,
    path: s.path,
    ownerModuleCode: 'crm_core',
    hasLayouts: true,
  })),
  ...BASE_PACKAGE_KARTOTEKI.map((k) => ({
    slug: k.slug,
    label: k.label,
    iconName: k.iconName,
    path: k.path,
    ownerModuleCode: k.slug === 'warehouses' ? 'warehouses' : 'crm_core',
    hasLayouts: true,
  })),
  {
    slug: 'ksef_integration',
    label: 'Dokumenty KSeF',
    iconName: 'ShieldCheck',
    path: '/ksef',
    ownerModuleCode: 'ksef_integration',
    hasLayouts: false,
  },
];

const TARGETS_BY_SLUG = new Map(SYSTEM_KARTOTEKA_TARGETS.map((t) => [t.slug, t]));

export function findSystemKartotekaTarget(slug: string): SystemKartotekaTarget | null {
  return TARGETS_BY_SLUG.get(slug) ?? null;
}

/**
 * Odpowiedź `GET /api/admin/module-templates/[code]/menu-source` — kartoteki do palety menu
 * modułu w kreatorze ITE: systemowe (z modułem-właścicielem) + z OPUBLIKOWANYCH modułów
 * kreatora. Własne kartoteki edytowanego modułu i ekrany (Funkcje) składa front lokalnie.
 */
export interface TemplateMenuSourceResponse {
  kartoteki: MenuSourceKartoteka[];
  /** Kod modułu → nazwa (oznaczenie modułu-właściciela ekranów w palecie). */
  moduleNames: Record<string, string>;
  /**
   * Kartoteki z innych opublikowanych modułów kreatora, dla których można przypiąć układy:
   * slug (`<szablon>_<encja>`) → UUID encji (klucz biblioteki) + pola z OPUBLIKOWANEJ wersji.
   */
  creatorKartoteki?: Record<string, CreatorKartotekaLayoutsInfo>;
}

export interface CreatorKartotekaLayoutsInfo {
  entityId: string;
  label: string;
  fields: Array<{
    slug: string;
    name: string;
    type: string;
    isRequired: boolean;
    sortOrder: number;
  }>;
}
